/**
 * BoxMaterialSection — right column for box-based material input.
 * Extracted from AddSessionModal.tsx for readability.
 */
import React, { useMemo, useRef } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { type RawMaterial, type MaterialSummary, type LocationBalance } from '../../../../services/productionService';
import { type ByproductMapping } from '../../../../services/byproductMappingService';
import { formatYield, isYieldFallback } from '../../../../hooks/useYieldFactor';
import { Trash2, ArrowUpDown, AlertTriangle } from 'lucide-react';
import type { Product, BoxMaterialEntry, MaterialSortOption } from './types';
import RawMaterialOverview from './RawMaterialOverview';
import MaterialSummaryBoxes from './MaterialSummaryBoxes';

interface BoxMaterialSectionProps {
  boxEntries: BoxMaterialEntry[];
  setBoxEntries: React.Dispatch<React.SetStateAction<BoxMaterialEntry[]>>;
  productSku: string;
  products: Product[];
  rawMaterials: RawMaterial[];
  sortedRawMaterials: RawMaterial[];
  loadingMaterials: boolean;
  materialSortOption: MaterialSortOption;
  setMaterialSortOption: (v: MaterialSortOption) => void;
  byproductMappings: ByproductMapping[];
  materialSummary: MaterialSummary;
  icsBalances?: Record<string, LocationBalance[]>;
  loadingIcsBalances?: boolean;
  sourceWarehouse?: string;
  stockAdjustments?: Map<string, number>;
}

// ===== Box mode helper functions =====

const EMPTY_BOX_ENTRY: BoxMaterialEntry = {
  item_number: '', transfer_number: '', item_name: '', target_boxes: '',
  available_stock: 0, yield_factor: 0, unit: 'lb', unit_weight: 0, is_on_hold: false,
};

export const createEmptyBoxEntry = (): BoxMaterialEntry => ({ ...EMPTY_BOX_ENTRY });

export const getEffectiveYield = (entry: BoxMaterialEntry): number => {
  return parseFloat(String(entry.yield_factor)) || 0;
};

export const getTotalAvailableStock = (itemNumber: string, rawMaterials: RawMaterial[]): number => {
  return rawMaterials
    .filter(m => m.item_number === itemNumber && (m.has_lot_inventory || m.is_on_hold))
    .reduce((sum, m) => sum + (parseFloat(String(m.current_stock)) || 0), 0);
};

export const calculateRequiredWeight = (
  entry: BoxMaterialEntry,
  products: Product[],
  productSku: string,
  rawMaterials: RawMaterial[],
): number => {
  const product = products.find(p => p.sku === productSku);
  const boxes = parseInt(entry.target_boxes) || 0;
  const yieldFactorRaw = getEffectiveYield(entry);
  if (!product?.unit_weight || !boxes) return 0;

  if (yieldFactorRaw && yieldFactorRaw > 0 && entry.unit_weight && entry.unit_weight > 0) {
    const targetOutput = boxes * product.unit_weight;
    const theoreticalInput = targetOutput / yieldFactorRaw;
    const totalAvailable = entry.is_on_hold
      ? getTotalAvailableStock(entry.item_number, rawMaterials)
      : (entry.available_stock || 0);
    const availableOutput = totalAvailable * yieldFactorRaw;

    if (availableOutput >= targetOutput) {
      return theoreticalInput;
    } else {
      const deficit = theoreticalInput - totalAvailable;
      const transferCases = Math.ceil(deficit / entry.unit_weight);
      return totalAvailable + (transferCases * entry.unit_weight);
    }
  }

  return 0;
};

export const calculateBoxTotals = (
  boxEntries: BoxMaterialEntry[],
  products: Product[],
  productSku: string,
  rawMaterials: RawMaterial[],
): { totalBoxes: number; totalRequiredWeight: number; totalUsableOutput: number } => {
  const product = products.find(p => p.sku === productSku);
  let totalBoxes = 0;
  let totalRequiredWeight = 0;

  boxEntries.forEach(entry => {
    if (entry.item_number && entry.target_boxes) {
      totalBoxes += parseInt(entry.target_boxes) || 0;
      totalRequiredWeight += calculateRequiredWeight(entry, products, productSku, rawMaterials);
    }
  });

  // Est. output = total input × yield (matches per-material Est. Output calculation)
  const avgYield = boxEntries.length > 0
    ? boxEntries.reduce((sum, e) => sum + (getEffectiveYield(e) || 0), 0) / boxEntries.filter(e => e.item_number).length
    : 0;
  const totalUsableOutput = totalRequiredWeight > 0 && avgYield > 0
    ? totalRequiredWeight * avgYield
    : (product?.unit_weight ? totalBoxes * product.unit_weight : 0);
  return { totalBoxes, totalRequiredWeight, totalUsableOutput };
};

export const hasBoxValidationErrors = (
  boxEntries: BoxMaterialEntry[],
  products: Product[],
  productSku: string,
  rawMaterials: RawMaterial[],
): boolean => {
  const product = products.find(p => p.sku === productSku);
  return boxEntries.some(entry => {
    if (!entry.item_number || !entry.target_boxes) return false;
    if (!entry.transfer_number) return false;
    const boxes = parseInt(entry.target_boxes) || 0;
    const yieldFactor = getEffectiveYield(entry);
    const targetOutput = product?.unit_weight ? boxes * product.unit_weight : 0;
    const totalAvailable = entry.is_on_hold
      ? getTotalAvailableStock(entry.item_number, rawMaterials)
      : (entry.available_stock || 0);
    const availableOutput = totalAvailable * (yieldFactor || 0);
    return availableOutput < targetOutput;
  });
};

export const isBoxFormValid = (
  boxEntries: BoxMaterialEntry[],
  products: Product[],
  productSku: string,
  rawMaterials: RawMaterial[],
  sourceWarehouse: string,
): boolean => {
  if (!productSku) return false;
  const selectedMaterials = boxEntries.filter(entry => entry.item_number);
  const hasValidMaterial = selectedMaterials.length > 0 && selectedMaterials.every(entry =>
    entry.target_boxes && parseInt(entry.target_boxes) > 0
  );
  if (!hasValidMaterial) return false;
  const needsTransfer = selectedMaterials.some(e =>
    !e.transfer_number || calculateRequiredWeight(e, products, productSku, rawMaterials) > e.available_stock
  );
  if (needsTransfer && !sourceWarehouse) return false;
  return true;
};

const BoxMaterialSection: React.FC<BoxMaterialSectionProps> = ({
  boxEntries,
  setBoxEntries,
  productSku,
  products,
  rawMaterials,
  sortedRawMaterials,
  loadingMaterials,
  materialSortOption,
  setMaterialSortOption,
  byproductMappings,
  materialSummary,
  icsBalances,
  loadingIcsBalances,
  sourceWarehouse,
  stockAdjustments,
}) => {
  const { t } = useLanguage();
  const lastDeselectedRef = useRef<{ item_number: string; target_boxes: string; available_stock: number; transfer_number: string } | null>(null);
  const lastAddedItemRef = useRef<string | null>(null);

  const updateBoxMaterial = (index: number, field: string, value: string) => {
    if (field === 'material') {
      if (!value) {
        setBoxEntries(prev => prev.map((entry, i) =>
          i === index ? createEmptyBoxEntry() : entry
        ));
        return;
      }
      const [itemNumber, transferNumberRaw] = value.split('|');
      const actualTransferNumber = transferNumberRaw || null;
      const material = rawMaterials.find(
        m => m.item_number === itemNumber && m.transfer_number === actualTransferNumber
      );
      if (material) {
        setBoxEntries(prev => prev.map((entry, i) => {
          if (i !== index) return entry;
          return {
            item_number: itemNumber,
            transfer_number: actualTransferNumber || '',
            item_name: material.item_english_name || '',
            target_boxes: entry.target_boxes,
            available_stock: material.current_stock,
            yield_factor: material.yield_factor || 0,
            yield_factor_source: material.yield_factor_source,
            unit: material.unit || 'lb',
            unit_weight: material.unit_weight || 0,
            is_on_hold: material.is_on_hold || false,
          };
        }));
      }
    } else if (field === 'target_boxes') {
      setBoxEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, target_boxes: value } : entry
      ));
    }
  };

  const getByproductsForMaterial = (itemNumber: string): ByproductMapping[] => {
    if (!itemNumber || !byproductMappings.length) return [];
    return byproductMappings.filter(m => m.raw_material_item_number === itemNumber);
  };

  // Build set of selected material keys for the overview table
  const selectedItemKeys = useMemo(() => {
    const keys = new Set<string>();
    boxEntries.forEach(e => {
      if (e.item_number) {
        keys.add(`${e.item_number}|${e.transfer_number || ''}|${e.location_code || ''}`);
        // Also add item_number-only key so edit-mode entries with auto-generated
        // transfer_numbers still highlight the raw material row
        keys.add(`${e.item_number}||`);
      }
    });
    return keys;
  }, [boxEntries]);

  // Handle clicking a row in the overview table
  const handleOverviewSelect = (material: RawMaterial) => {
    const key = `${material.item_number}|${material.transfer_number || ''}|${material.location_code || ''}`;

    // If already selected, deselect it (remove the entry)
    // Try exact 3-part key match first, then fall back to item_number-only (for edit mode where transfer_number may differ)
    let existingIdx = boxEntries.findIndex(
      e => e.item_number === material.item_number && e.transfer_number === (material.transfer_number || '') && (e.location_code || '') === (material.location_code || '')
    );
    if (existingIdx < 0) {
      existingIdx = boxEntries.findIndex(e => e.item_number === material.item_number);
    }
    if (existingIdx >= 0) {
      const removedEntry = boxEntries[existingIdx];
      if (boxEntries.length <= 1) {
        // Only one entry — clear item_number but preserve target_boxes
        setBoxEntries([{ ...createEmptyBoxEntry(), target_boxes: removedEntry.target_boxes }]);
      } else {
        setBoxEntries(prev => prev.filter((_, i) => i !== existingIdx));
      }
      // Stash values so re-selecting the same material restores them
      lastDeselectedRef.current = { item_number: material.item_number, target_boxes: removedEntry.target_boxes, available_stock: removedEntry.available_stock, transfer_number: removedEntry.transfer_number };
      return;
    }

    // Find the first empty entry slot
    const emptyIdx = boxEntries.findIndex(e => !e.item_number);
    // Restore target_boxes if re-selecting a just-deselected material
    const restoredBoxes = lastDeselectedRef.current?.item_number === material.item_number
      ? lastDeselectedRef.current.target_boxes
      : '';
    // Restore values from stash if re-selecting (preserves edit-mode lot-backed allocation + transfer info)
    const isReselect = lastDeselectedRef.current?.item_number === material.item_number;
    const restoredStock = isReselect ? lastDeselectedRef.current!.available_stock : material.current_stock;
    const restoredTransfer = isReselect ? lastDeselectedRef.current!.transfer_number : (material.transfer_number || '');
    const newEntry: BoxMaterialEntry = {
      item_number: material.item_number,
      transfer_number: restoredTransfer,
      item_name: material.item_english_name || '',
      target_boxes: restoredBoxes,
      available_stock: restoredStock,
      yield_factor: material.yield_factor || 0,
      yield_factor_source: material.yield_factor_source,
      unit: material.unit || 'lb',
      unit_weight: material.unit_weight || 0,
      is_on_hold: material.is_on_hold || false,
      location_code: material.location_code || '',
    };

    lastAddedItemRef.current = material.item_number;
    if (emptyIdx >= 0) {
      // Fill the empty slot, preserving any target_boxes the user already entered
      setBoxEntries(prev => prev.map((e, i) => i === emptyIdx ? { ...newEntry, target_boxes: e.target_boxes || newEntry.target_boxes } : e));
    } else {
      // Add a new entry
      setBoxEntries(prev => [...prev, newEntry]);
    }
  };

  const selectedEntries = boxEntries.filter(e => e.item_number);

  return (
    <div className="space-y-3">
      {/* Header with sort */}
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('supervisor.material')} *
        </label>
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={materialSortOption}
            onChange={(e) => setMaterialSortOption(e.target.value as MaterialSortOption)}
            disabled={!productSku}
            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
          >
            <option value="preference">{t('supervisor.sortPreference')}</option>
            <option value="yield_high">{t('supervisor.sortYieldHigh')}</option>
            <option value="yield_low">{t('supervisor.sortYieldLow')}</option>
            <option value="labor_high">{t('supervisor.sortLaborHigh')}</option>
            <option value="labor_low">{t('supervisor.sortLaborLow')}</option>
            <option value="availability_high">{t('supervisor.sortAvailabilityHigh')}</option>
            <option value="availability_low">{t('supervisor.sortAvailabilityLow')}</option>
          </select>
        </div>
      </div>

      {/* MaterialSummaryBoxes removed — total shown at bottom of RawMaterialOverview */}

      {/* Raw Material Overview Table — click to select/deselect */}
      {productSku && (
        <RawMaterialOverview
          sortedRawMaterials={sortedRawMaterials}
          selectedItemKeys={selectedItemKeys}
          onSelectMaterial={handleOverviewSelect}
          loading={loadingMaterials}
          icsBalances={icsBalances}
          loadingIcsBalances={loadingIcsBalances}
          sourceWarehouse={sourceWarehouse}
          stockAdjustments={stockAdjustments}
        />
      )}

      {!productSku && (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
          {t('common.selectProductFirst') || 'Select a finished product first'}
        </p>
      )}


      {/* Selected Material Detail Cards */}
      {selectedEntries.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('supervisor.selectedMaterials') || 'Selected Materials'}
          </div>

          {boxEntries.map((entry, index) => {
            if (!entry.item_number) return null;
            const entryBoxes = parseInt(entry.target_boxes) || 0;
            const requiredWeight = calculateRequiredWeight(entry, products, productSku, rawMaterials);
            const effectiveYield = getEffectiveYield(entry);
            const product = products.find(p => p.sku === productSku);
            const targetOutput = product?.unit_weight ? entryBoxes * product.unit_weight : 0;
            const totalAvailable = entry.is_on_hold
              ? getTotalAvailableStock(entry.item_number, rawMaterials)
              : (entry.available_stock || 0);
            const availableOutput = totalAvailable * (effectiveYield || 0);
            const weightExceedsStock = entry.item_number && entryBoxes > 0 && availableOutput < targetOutput;
            const isBoxEntryFallback = isYieldFallback(entry.yield_factor_source);
            const yieldDisplay = effectiveYield
              ? formatYield(effectiveYield, undefined, entry.yield_factor_source)
              : null;
            // Compute displayCases using the hteCases + transferCases logic (same as tooltip)
            const fromHteForCases = Math.min(totalAvailable, requiredWeight);
            const shortfallForCases = Math.max(0, requiredWeight - fromHteForCases);
            const hteCasesForDisplay = entry.unit_weight > 0 ? Math.floor(fromHteForCases / entry.unit_weight) : 0;
            const transferCasesForDisplay = entry.unit_weight > 0 ? Math.ceil(shortfallForCases / entry.unit_weight) : 0;
            const displayCases = (entryBoxes && effectiveYield && requiredWeight && entry.unit_weight)
              ? hteCasesForDisplay + transferCasesForDisplay : 0;
            const sourceLoc = sourceWarehouse
              ? icsBalances?.[entry.item_number]?.find(l => l.locationCode === sourceWarehouse)
              : undefined;
            const sourceAvailCases = sourceLoc?.displayCasesPieces?.cases ?? null;
            const exceedsSourceBalance = sourceAvailCases !== null && displayCases > 0 && displayCases > sourceAvailCases;

            return (
              <div key={index} className="px-3 py-2 border border-indigo-200 dark:border-indigo-700/50 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20 space-y-2">
                {/* Material header with name and remove button */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {index === 0 && (
                      <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded">
                        {t('supervisor.primaryMaterial') || 'Primary Material'}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {entry.item_number} - {entry.item_name}
                    </span>
                    {weightExceedsStock && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                        <AlertTriangle className="w-3 h-3" />
                        {t('supervisor.partialTransferNeeded')}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOverviewSelect(rawMaterials.find(
                      m => m.item_number === entry.item_number && m.transfer_number === (entry.transfer_number || null)
                    ) || { item_number: entry.item_number, transfer_number: entry.transfer_number || null } as RawMaterial)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Remove material"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Material Info Row */}
                <div className={`grid gap-3 ${selectedEntries.length === 1 ? 'grid-cols-6' : 'grid-cols-7'}`}>
                  {/* Available Stock */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.availableStock') || 'Available'}</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.available_stock.toFixed(2)} {entry.unit}</p>
                  </div>

                  {/* Yield Factor */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('operator.yield')}</span>
                    <p className={`text-sm font-medium ${
                      !effectiveYield
                        ? 'text-gray-400 dark:text-gray-500'
                        : isBoxEntryFallback
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-900 dark:text-white'
                    }`}>
                      {yieldDisplay || '?'}
                    </p>
                    {!effectiveYield && parseInt(entry.target_boxes) > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 dark:text-red-400">
                        <AlertTriangle className="w-3 h-3" />
                        {t('supervisor.yieldNotConfigured')}
                      </span>
                    )}
                  </div>

                  {/* Raw Material Unit Weight */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.unitWeight') || 'Unit Weight'}</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.unit_weight ? `${Number(entry.unit_weight).toFixed(2)} lbs` : '-'}</p>
                  </div>

                  {/* Boxes Input - only show for multiple materials */}
                  {selectedEntries.length > 1 && (
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.cases') || 'Cases'}</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={entry.target_boxes}
                        onChange={(e) => updateBoxMaterial(index, 'target_boxes', e.target.value)}
                        autoFocus={lastAddedItemRef.current === entry.item_number}
                        onFocus={() => { if (lastAddedItemRef.current === entry.item_number) lastAddedItemRef.current = null; }}
                        className={`w-full px-2 py-1 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm ${
                          weightExceedsStock
                            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          } text-gray-900 dark:text-white`}
                      />
                    </div>
                  )}

                  {/* Est. Cases — hover shows HTE/Transfer breakdown */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.estCases') || 'Est. Cases'}</span>
                    {(() => {
                      if (!entryBoxes || !effectiveYield || !requiredWeight || !entry.unit_weight) {
                        return <p className="text-sm font-medium text-gray-400">-</p>;
                      }
                      const fromHte = Math.min(totalAvailable, requiredWeight);
                      const shortfall = requiredWeight - fromHte;
                      const transferCases = entry.unit_weight > 0 ? Math.ceil(shortfall / entry.unit_weight) : 0;
                      const hteCases = entry.unit_weight > 0 ? Math.floor(fromHte / entry.unit_weight) : 0;
                      const hteRemainder = fromHte - (hteCases * (entry.unit_weight || 0));

                      if (weightExceedsStock && transferCases > 0) {
                        return (
                          <div className="relative group">
                            <p className={`text-sm font-medium underline decoration-dotted ${exceedsSourceBalance ? 'text-red-600 dark:text-red-400' : 'text-purple-600 dark:text-purple-400'}`}>
                              {hteCases + transferCases}{hteRemainder > 0.1 ? '+' : ''}
                            </p>
                            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                                <p><span className="text-green-400">HTE:</span> {hteCases > 0 ? `${hteCases} cs` : ''}{hteRemainder > 0.1 ? `${hteCases > 0 ? ' + ' : ''}${hteRemainder.toFixed(2)} lbs` : ''}</p>
                                <p><span className="text-amber-400">Transfer:</span> {transferCases} cs</p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="relative group">
                          <p className="text-sm font-medium text-purple-600 dark:text-purple-400 underline decoration-dotted">
                            {displayCases}
                          </p>
                          <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                              <p><span className="text-green-400">HTE:</span> {hteCases} cs</p>
                              <p className="text-gray-400">No inbound transfer needed</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Est. Input — hover shows HTE/Transfer breakdown */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.estInput') || 'Est. Input'}</span>
                    {(() => {
                      if (!entryBoxes) return <p className="text-sm font-medium text-gray-400">-</p>;
                      if (!effectiveYield) return <p className="text-sm font-medium text-gray-400">?</p>;
                      if (requiredWeight <= 0) return <p className="text-sm font-medium text-gray-400">-</p>;

                      const fromHte = Math.min(totalAvailable, requiredWeight);
                      const shortfall = requiredWeight - fromHte;
                      const transferCases = entry.unit_weight > 0 ? Math.ceil(shortfall / entry.unit_weight) : 0;
                      const fromTransfer = transferCases * (entry.unit_weight || 0);
                      const totalWeight = fromHte + fromTransfer;

                      if (weightExceedsStock && fromTransfer > 0) {
                        return (
                          <div className="relative group">
                            <p className="text-sm font-medium text-orange-600 dark:text-orange-400 underline decoration-dotted">
                              {totalWeight.toFixed(2)}
                            </p>
                            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                                <p><span className="text-green-400">HTE:</span> {fromHte.toFixed(2)} lbs</p>
                                <p><span className="text-amber-400">Transfer:</span> {fromTransfer.toFixed(2)} lbs</p>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="relative group">
                          <p className="text-sm font-medium text-orange-600 dark:text-orange-400 underline decoration-dotted">
                            {requiredWeight.toFixed(2)}
                          </p>
                          <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                              <p><span className="text-green-400">HTE:</span> {fromHte.toFixed(2)} lbs</p>
                              <p className="text-gray-400">No inbound transfer needed</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Est. Output — actual input × yield */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.estOutput') || 'Est. Output'}</span>
                    {(() => {
                      if (!entryBoxes || !effectiveYield || requiredWeight <= 0) {
                        return <p className="text-sm font-medium text-gray-400">-</p>;
                      }
                      const output = requiredWeight * effectiveYield;
                      return (
                        <div className="relative group">
                          <p className="text-sm font-medium text-green-600 dark:text-green-400 underline decoration-dotted">
                            {output.toFixed(2)}
                          </p>
                          <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-50">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                              <p>{requiredWeight.toFixed(2)} lbs × {(effectiveYield * 100).toFixed(2)}% = {output.toFixed(2)} lbs</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>


                {/* VB6 source balance warning */}
                {exceedsSourceBalance && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      {t('supervisor.insufficientSourceStock') || 'Insufficient stock at'} {sourceLoc?.locationName || sourceWarehouse} ({sourceWarehouse}): {t('supervisor.onlyHas') || 'only has'} {sourceAvailCases} CS — {displayCases} CS {t('supervisor.needed') || 'needed'}
                    </p>
                  </div>
                )}

                {/* Linked By-Products */}
                {(() => {
                  const linkedBp = getByproductsForMaterial(entry.item_number);
                  if (!linkedBp.length) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                        {t('supervisor.byproducts')}
                      </span>
                      {linkedBp.map((bp, idx) => (
                        <React.Fragment key={bp.id}>
                          {idx > 0 && <span className="text-xs text-gray-300 dark:text-gray-600">|</span>}
                          <span className="text-xs text-gray-700 dark:text-gray-300">
                            {bp.byproduct_item_number}{bp.byproduct_name ? ` - ${bp.byproduct_name}` : ''}{bp.byproduct_chinese_name ? ` / ${bp.byproduct_chinese_name}` : ''}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Totals Summary */}
      {boxEntries.some(e => e.item_number && e.target_boxes && parseInt(e.target_boxes) > 0) && (() => {
        const boxTotals = calculateBoxTotals(boxEntries, products, productSku, rawMaterials);
        const hasAnyMissingYield = boxEntries.some(e => {
          if (!e.item_number || !e.target_boxes || parseInt(e.target_boxes) <= 0) return false;
          return !getEffectiveYield(e);
        });

        return (
          <div className="flex justify-end gap-6 pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalCases') || 'Total Cases'}:</span>
              <span className="ml-2 font-medium text-indigo-600 dark:text-indigo-400">
                {(() => {
                  let totalWeight = 0;
                  let totalUnitWeight = 0;
                  for (const e of boxEntries) {
                    if (!e.item_number || !e.target_boxes) continue;
                    const rw = calculateRequiredWeight(e, products, productSku, rawMaterials);
                    totalWeight += rw;
                    if (e.unit_weight > 0) totalUnitWeight = e.unit_weight;
                  }
                  const fullCases = totalUnitWeight > 0 ? Math.floor(totalWeight / totalUnitWeight) : 0;
                  const hasPartial = totalUnitWeight > 0 && totalWeight % totalUnitWeight > 0.01;
                  return `${fullCases}${hasPartial ? '+' : ''}`;
                })()}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalInputWeight') || 'Total Input Weight'}:</span>
              <span className={`ml-2 font-medium ${hasAnyMissingYield ? 'text-gray-400 dark:text-gray-500' : 'text-orange-600 dark:text-orange-400'}`}>
                {hasAnyMissingYield ? '?' : `${boxTotals.totalRequiredWeight.toFixed(2)} lbs`}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalOutputWeight') || 'Total Output Weight'}:</span>
              <span className={`ml-2 font-medium ${hasAnyMissingYield ? 'text-gray-400 dark:text-gray-500' : 'text-green-600 dark:text-green-400'}`}>
                {hasAnyMissingYield ? '?' : `${boxTotals.totalUsableOutput.toFixed(2)} lbs`}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BoxMaterialSection;
